import { useCallback, useEffect, useRef } from 'react';
import { useLogoutMutation } from '../Api/authentication';
import { useOrganizersLogoutMutation } from '../Api/organizers';
import { isUserSessionActive } from '../Helpers/user-session-expiration';
import useLogout from '../Hooks/useLogout';
import notify from '../Style-guide/ToastMessage';
import { useGlobalState } from './globalState';

const LOGOUT_MESSAGE = 'Session expired. Please log in again.';

const SessionGuard = () => {
  const { globalState } = useGlobalState();

  const { mutate: userLogoutRequest } = useLogoutMutation();
  const { mutate: organizerLogoutRequest } = useOrganizersLogoutMutation();

  const logout = useLogout();

  // only prevents duplicate 401-triggered logout
  const unauthorizedTriggered = useRef(false);

  const handleLogout = useCallback(
    (type) => {
      const isOrganizer = type === 'organizer';

      logout({
        redirectPath: isOrganizer ? '/organizers' : '/signin',
        logoutRequest: isOrganizer ? organizerLogoutRequest : userLogoutRequest,
        globalStateKey: isOrganizer ? 'organizer' : 'user',
      });

      notify.info(LOGOUT_MESSAGE);
    },
    [logout, organizerLogoutRequest, userLogoutRequest]
  );

  // Passive expiration check
  useEffect(() => {
    // ignore while unauthorized flow is already handling logout
    if (unauthorizedTriggered.current) return;

    const user = globalState?.user;
    const organizer = globalState?.organizer;

    if (user?.id && !isUserSessionActive(user.ses_exp)) {
      handleLogout('user');
    } else if (organizer?.id && !isUserSessionActive(organizer.ses_exp)) {
      handleLogout('organizer');
    }
  }, [globalState, handleLogout]);

  // Active unauthorized monitoring
  useEffect(() => {
    const onUnauthorized = () => {
      // prevent duplicate event dispatches
      if (unauthorizedTriggered.current) return;

      unauthorizedTriggered.current = true;

      const isOrganizerPath = window.location.pathname.startsWith('/organizers');

      handleLogout(isOrganizerPath ? 'organizer' : 'user');
    };

    window.addEventListener('session-expired', onUnauthorized);

    return () => {
      window.removeEventListener('session-expired', onUnauthorized);
    };
  }, [handleLogout]);

  return null;
};

export default SessionGuard;
